/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import useRouter from 'js/lib/useRouter';

import { Grid, PageGridContainer, useTheme } from '@coursera/cds-core';

import ResponsiveTab from 'bundles/enterprise-legacy-learner-home/components/myLearning/ResponsiveTab';
import type { OnProductCardClick } from 'bundles/enterprise-legacy-learner-home/types/Products';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

import SkillsDashboard from './skillsDashboard/SkillsDashboard';

const useStyles = () => {
  const { spacing, breakpoints, palette } = useTheme();
  return {
    container: css`
      display: flex;
      flex-wrap: wrap;
      padding: ${spacing(48)} 0;
      margin: ${spacing(48)};

      ${breakpoints.down('sm')} {
        padding: ${spacing(8)};
      }
    `,
    tab: css`
      padding-top: 0 !important; /* override CDS padding for better alignment */
      flex: 1 1 17%;
      ${breakpoints.down('md')} {
        flex: 1 1 100%;
      }
    `,
    content: css`
      padding: ${spacing(24)};
      max-width: 100%;
      ${breakpoints.up('md')} {
        flex: 1 1 83%;
        border: 1px solid ${palette.gray[300]};
        box-shadow: 0 1px 6px ${palette.gray[300]}, 0 1px 4px ${palette.gray[300]};
        background: ${palette.white};
      }
      ${breakpoints.down('sm')} {
        padding: ${spacing(8)};
      }
    `,
  };
};

const useQueryParamState = <T,>(queryParamName: string, defaultValue: T, validValues: T[]) => {
  const router = useRouter();
  // Initialize to the current query parameter value or default value
  const valueFromRouter = router.location.query[queryParamName];
  const state = useState<T>(() => (validValues.includes(valueFromRouter) ? valueFromRouter : defaultValue));

  useEffect(() => {
    // Whenever the value changes, update the router
    router.replace({
      ...router.location,
      query: {
        ...router.location.query,
        [queryParamName]: state[0],
      },
    });

    // When unmounting, clear the value of query parameter

    return () => {
      router.replace({
        ...router.location,
        query: {
          ...router.location.query,
          [queryParamName]: undefined,
        },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state[0]]);

  return state;
};

type Props = {
  userId: number;
  onProductCardClick: OnProductCardClick;
  myCoursesContent: JSX.Element;
  programSlug: string;
  programId?: string;
};

export type MyLearningTabs = 'MY_COURSES' | 'SKILLS_DASHBOARD';

const MyLearningTabContent = (props: Props) => {
  const [selectedTab, setSelectedTab] = useQueryParamState<MyLearningTabs>('myLearningTab', 'MY_COURSES', [
    'MY_COURSES',
    'SKILLS_DASHBOARD',
  ]);
  const styles = useStyles();

  return (
    <div css={styles.container}>
      <PageGridContainer>
        <Grid item xs={12} lg={2} css={styles.tab}>
          <ResponsiveTab
            tabs={{
              MY_COURSES: _t('My Courses'),
              SKILLS_DASHBOARD: _t('All Skills'),
            }}
            selectedTab={selectedTab}
            onTabSelected={setSelectedTab}
          />
        </Grid>
        <Grid item xs={12} lg={10} css={styles.content}>
          {selectedTab === 'MY_COURSES' && props.myCoursesContent}
          {selectedTab === 'SKILLS_DASHBOARD' && (
            <SkillsDashboard programId={props.programId} programSlug={props.programSlug} />
          )}
        </Grid>
      </PageGridContainer>
    </div>
  );
};

export default MyLearningTabContent;
